import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, ArrowRight, Home, LogIn, LogOut, Menu, Route, User, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

type AppTab = 'planned-trips' | 'planner' | 'trip-history' | 'services' | 'profile';

interface TopNavProps {
  variant?: 'landing' | 'app';
  currentTab?: AppTab;
  onNavigateToLanding?: () => void;
  onNavigateToPlanner?: () => void;
  onNavigateToPlannedTrips?: () => void;
  onNavigateToTripHistory?: () => void;
  onNavigateToServices?: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToEmergency?: () => void;
  onOpenAuth?: (view: 'login' | 'register') => void;
  hideAuthActions?: boolean;
}

const APP_TABS: Array<{ id: AppTab; label: string }> = [
  { id: 'planned-trips', label: 'Planificados' },
  { id: 'planner', label: 'Programar' },
  { id: 'trip-history', label: 'Mis viajes' },
  { id: 'services', label: 'Servicios' },
];

export const TopNav: React.FC<TopNavProps> = ({
  variant = 'app',
  currentTab = 'planner',
  onNavigateToLanding,
  onNavigateToPlanner,
  onNavigateToPlannedTrips,
  onNavigateToTripHistory,
  onNavigateToServices,
  onNavigateToProfile,
  onNavigateToEmergency,
  onOpenAuth,
  hideAuthActions = false,
}) => {
  const { user, profile, signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigateAppTab = (tab: AppTab) => {
    if (tab === 'planned-trips') onNavigateToPlannedTrips?.();
    if (tab === 'planner') onNavigateToPlanner?.();
    if (tab === 'trip-history') onNavigateToTripHistory?.();
    if (tab === 'services') onNavigateToServices?.();
    setIsMobileMenuOpen(false);
  };

  const handleSignOut = async () => {
    await signOut();
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
    onNavigateToLanding?.();
  };

  const userInitial = (profile?.full_name?.[0] || user?.email?.[0] || 'U').toUpperCase();

  return (
    <>
      <nav className="sticky top-0 z-50 bg-white/85 backdrop-blur-md border-b border-gray-100 px-6 py-3">
        <div className="max-w-6xl mx-auto flex justify-between items-center gap-4">

          {/* Logo */}
          <button
            className="flex items-center gap-2 hover:opacity-80 transition-opacity shrink-0"
            onClick={onNavigateToLanding}
          >
            <div className="bg-blue-600 p-2 rounded-lg shadow-sm">
              <Route className="text-white" size={20} />
            </div>
            <span className="text-lg font-bold text-gray-900 hidden lg:inline">ProgramatuViaje.cl</span>
          </button>

          {/* Tabs principales — solo en app */}
          {variant === 'app' && (
            <div className="hidden md:flex items-center gap-1 bg-gray-100 rounded-full px-1.5 py-1">
              {APP_TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => navigateAppTab(tab.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap ${
                    currentTab === tab.id
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* Derecha: avatar dropdown (app) o auth buttons (landing) */}
          <div className="hidden md:flex items-center gap-2 shrink-0">
            {variant === 'app' && user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(prev => !prev)}
                  className="w-9 h-9 rounded-full bg-blue-600 text-white font-bold text-sm flex items-center justify-center hover:bg-blue-700 transition-colors"
                >
                  {userInitial}
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 top-11 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 w-52 z-50">
                    <div className="px-4 py-2.5 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {profile?.full_name || user.email?.split('@')[0]}
                      </p>
                      <p className="text-xs text-gray-400 truncate">{user.email}</p>
                    </div>
                    <button
                      onClick={() => { onNavigateToProfile?.(); setIsUserMenuOpen(false); }}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                    >
                      <User size={15} className="text-gray-400" />
                      Perfil
                    </button>
                    <button
                      onClick={() => { onNavigateToEmergency?.(); setIsUserMenuOpen(false); }}
                      className="w-full text-left px-4 py-2.5 text-sm text-orange-600 hover:bg-orange-50 transition-colors flex items-center gap-2"
                    >
                      <AlertTriangle size={15} />
                      Emergencias
                    </button>
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <button
                        onClick={handleSignOut}
                        className="w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors flex items-center gap-2"
                      >
                        <LogOut size={15} />
                        Cerrar sesión
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : hideAuthActions ? null : variant === 'landing' ? (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => onOpenAuth?.('login')}
                  className="text-gray-600 hover:text-gray-900 font-medium px-3 py-2 flex items-center gap-2 text-sm"
                >
                  <LogIn size={15} />
                  Login
                </button>
                <button
                  onClick={onNavigateToPlanner}
                  className="bg-blue-600 text-white px-4 py-2 rounded-xl font-bold text-sm shadow-md shadow-blue-200 hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                >
                  Programa tu viaje
                  <ArrowRight size={13} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => onOpenAuth?.('login')}
                className="text-sm font-semibold text-blue-600 hover:text-blue-700"
              >
                Login
              </button>
            )}
          </div>

          {/* Hamburguesa móvil */}
          <button
            className="md:hidden p-2 text-gray-700"
            onClick={() => setIsMobileMenuOpen(prev => !prev)}
            aria-label="Abrir menú"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Menú móvil */}
      <div
        className={`fixed inset-0 z-40 bg-white transition-transform duration-200 md:hidden pt-20 px-6 ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="space-y-2">
          <button
            onClick={() => { onNavigateToLanding?.(); setIsMobileMenuOpen(false); }}
            className="w-full text-left rounded-xl px-4 py-3 bg-gray-100 text-gray-700 font-semibold inline-flex items-center gap-3"
          >
            <Home size={18} />
            Inicio
          </button>

          {variant === 'app' && APP_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => navigateAppTab(tab.id)}
              className={`w-full text-left rounded-xl px-4 py-3 font-semibold ${
                currentTab === tab.id ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}

          {variant === 'app' && (
            <>
              <button
                onClick={() => { onNavigateToProfile?.(); setIsMobileMenuOpen(false); }}
                className="w-full text-left rounded-xl px-4 py-3 bg-gray-100 text-gray-700 font-semibold inline-flex items-center gap-3"
              >
                <User size={18} />
                Perfil
              </button>
              <button
                onClick={() => { onNavigateToEmergency?.(); setIsMobileMenuOpen(false); }}
                className="w-full text-left rounded-xl px-4 py-3 bg-orange-50 text-orange-700 font-semibold inline-flex items-center gap-3"
              >
                <AlertTriangle size={18} />
                Emergencias
              </button>
            </>
          )}
        </div>

        {!hideAuthActions && (
        <div className="mt-6">
          {user ? (
            <button
              onClick={handleSignOut}
              className="w-full rounded-xl px-4 py-3 border border-gray-200 text-red-500 font-semibold inline-flex items-center justify-center gap-2"
            >
              <LogOut size={16} />
              Cerrar sesión
            </button>
          ) : (
            <div className="space-y-2">
              <button
                onClick={() => { onNavigateToPlanner?.(); setIsMobileMenuOpen(false); }}
                className="w-full rounded-xl px-4 py-3 bg-blue-600 text-white font-semibold inline-flex items-center justify-center gap-2"
              >
                Programa tu viaje
                <ArrowRight size={16} />
              </button>
              <button
                onClick={() => { onOpenAuth?.('login'); setIsMobileMenuOpen(false); }}
                className="w-full rounded-xl px-4 py-3 border border-gray-200 text-gray-700 font-semibold inline-flex items-center justify-center gap-2"
              >
                <LogIn size={16} />
                Login
              </button>
            </div>
          )}
        </div>
        )}
      </div>
    </>
  );
};
